import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Paintbrush } from 'lucide-react';
import SEO from '../components/SEO';
import BeforeAfterSlider from '../components/BeforeAfterSlider';
import ScrollAnimate from '../components/ScrollAnimate';
import { beforeAfterMock } from '../data';

export default function FoerEfter() {
  const [items, setItems] = useState(beforeAfterMock);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/content/before_after')
      .then((res) => {
        if (!res.ok) throw new Error('Kunne ikke hente før/efter billeder');
        return res.json();
      })
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          // Sorter efter 'order' feltet og tilføj det nye hvid/rosa projekt forrest
          const newProject = {
            id: "hvid-til-rosa",
            title: { da: "Maling af køkkenvæg (fra hvid til rosa)" },
            before_image: ["https://assets.auxodev.dk/assets/656b4e51-500c-4bf5-9ac7-27acb4e8635e/img-7112.jpeg"],
            after_image: ["https://assets.auxodev.dk/assets/656b4e51-500c-4bf5-9ac7-27acb4e8635e/img-7153.jpeg"],
            order: -1
          };
          const sorted = [newProject, ...data.filter(item => item.id !== 'hvid-til-rosa')].sort((a, b) => (a.order || 0) - (b.order || 0));
          setItems(sorted);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error('Fejl ved hentning af før/efter billeder:', err);
        setLoading(false);
      });
  }, []);

  return (
    <>
      <SEO 
        title="Før / efter billeder - Se vores malerarbejde | Syddansk Malerfirma"
        description="Se de fantastiske forvandlinger med vores før/efter billeder. Vi udfører professionelt malerarbejde i Sønderborg, Gråsten og på Als med fokus på detaljen."
      />

      {/* Hero Header */}
      <section className="relative bg-stone-50/50 py-16 md:py-24 border-b border-stone-100 overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute top-1/2 right-0 translate-x-1/4 -translate-y-1/2 text-stone-200/40 pointer-events-none z-0">
          <Paintbrush className="w-[500px] h-[500px] rotate-45 opacity-10" />
        </div>
        
        <div className="max-w-[1600px] mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
          <div className="max-w-3xl border-l-4 border-orange-500 pl-6 py-2">
            <span className="text-orange-500 font-bold tracking-wider uppercase text-sm mb-2 block flex items-center gap-2">
              <Sparkles className="w-4 h-4" /> Vores arbejde taler for sig selv
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-slate-800 tracking-tight leading-[1.15] mb-6">
              Her kan du se før og efter billeder af nogle af de projekter vi har lavet
            </h1>
            <p className="text-lg md:text-xl font-medium text-teal-900 leading-relaxed">
              Vores professionelle malerarbejde gør en markant forskel for rum, overflader og detaljer.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-[1600px] mx-auto px-6 sm:px-8 lg:px-12">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mb-4"></div>
              <p className="text-stone-500 font-medium">Henter før/efter billeder...</p>
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-20 bg-stone-50 rounded-3xl border border-stone-100">
              <p className="text-stone-500 text-lg font-medium">Der blev ikke fundet nogle før/efter billeder i øjeblikket.</p>
              <Link to="/" className="mt-4 inline-flex items-center gap-2 text-orange-500 font-bold hover:underline">
                Gå til forsiden <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 max-w-[1600px] mx-auto">
              {items.map((item, index) => (
                <ScrollAnimate 
                  key={item.id} 
                  direction="up" 
                  delay={index * 0.1} 
                  duration={0.8}
                >
                  <div>
                    <BeforeAfterSlider 
                      beforeImage={item.before_image?.[0]} 
                      afterImage={item.after_image?.[0]} 
                      title={item.title?.da}
                      position={item.title?.da?.toLowerCase().includes('loft') ? 'top' : 'center'}
                    />
                  </div>
                </ScrollAnimate>
              ))}
            </div>
          )}

          {/* Bottom CTA Card */}
          <ScrollAnimate direction="up" delay={0.2} duration={0.8} className="mt-20">
            <div className="bg-teal-900 text-white rounded-[2rem] p-8 md:p-16 text-center relative overflow-hidden shadow-xl">
              <div className="absolute -right-10 -bottom-10 w-64 h-64 bg-orange-500/10 rounded-full blur-3xl pointer-events-none"></div>
              <div className="absolute -left-10 -top-10 w-64 h-64 bg-teal-950/30 rounded-full blur-3xl pointer-events-none"></div>
              
              <div className="relative z-10 max-w-3xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-black mb-6 tracking-tight">Skal vi også forvandle dit hjem eller kontor?</h2>
                <p className="text-teal-100/90 text-lg mb-10 leading-relaxed">
                  Uanset om det drejer sig om indendørs spartling og maling eller en komplet udendørs renovering af facade og træværk, står vi klar med professionel rådgivning og et uforpligtende tilbud.
                </p>
                <Link 
                  to="/kontakt" 
                  className="inline-flex items-center justify-center rounded-full bg-[#ff6a00] px-10 py-4 text-lg font-bold text-white shadow-lg hover:bg-[#e05d00] transition-colors active:scale-95"
                >
                  Få et uforpligtende tilbud
                </Link>
              </div>
            </div>
          </ScrollAnimate>
        </div>
      </section>
    </>
  );
}
